// src/pages/auth/Login.js

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import './Login.css';
import axios from '../../api/axios';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const { login } = useContext(AuthContext);
  const [errorMessage, setErrorMessage] = useState('');
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    // Read reason set by interceptor
    try {
      const reason = sessionStorage.getItem('auth:reason');
      if (reason === 'expired') setExpired(true);
      sessionStorage.removeItem('auth:reason');
    } catch {}

    // Scrub any query params if present
    if (window.location.search) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const res = await axios.get('/api/publications');
        // const data = await res.json();
        setCarouselSlides(res.data);
      } catch (error) {
        console.error('Failed to fetch publications:', error);
      }
    };

    fetchPublications();
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) =>
        prev === carouselSlides.length - 1 ? 0 : prev + 1,
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselSlides.length]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    try {
      const data = await login(email, password);
      if (data.role === 'admin' || data.role === 'superadmin')
        navigate('/AdminDashboard');
      else if (data.role === 'alumni') navigate('/AlumniDashboard');
    } catch (err) {
      if (err.message === 'Account is deactivated. Contact support.') {
        setErrorMessage(
          'Your account has been deactivated. Please contact support.',
        );
      } else if (err.status === 403) {
        // fallback: maybe alumni not yet approved
        navigate('/pendingapproval');
      } else if (err.message === 'Incorrect password') {
        setErrorMessage('The password you entered is incorrect.');
      } else if (err.message === 'User not found') {
        setErrorMessage('No account found with this email.');
      } else {
        console.error('Login error:', err);
        setErrorMessage('Login failed. Please check your credentials.');
      }

      // Auto-clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 5000);
    }
  };

  return (
    <div className="login-wrapper">
      {expired && (
        <div className="session-alert">
          <span>⚠️ Your session expired. Please log in again.</span>
        </div>
      )}
      <div className="login-page">
        <div className="header">
          <img src="/smulogo.png" alt="SMU Logo" className="header-logo" />
          <h1 className="header-title">Saint Mary's University</h1>
        </div>

        <div className="login-container">
          {/* Carousel */}
          <div className="login-carousel-container">
            {carouselSlides.length > 0 ? (
              carouselSlides.map((slide, index) => (
                <div
                  key={index}
                  className={`carousel-slide ${
                    index === currentSlide ? 'active' : ''
                  }`}
                  style={{
                    backgroundImage: `url(${
                      process.env.REACT_APP_API_URL ||
                      `${window.location.protocol}//${window.location.hostname}:5001`
                    }/${slide.image?.filePath})`,
                  }}
                >
                  <div className="login-carousel-content">
                    <h2>{slide.title}</h2>
                    <p>{slide.description}</p>
                  </div>
                </div>
              ))
            ) : (
              // 👉 Fallback when there's no publication material
              <div className="carousel-slide fallback active">
                <div className="login-carousel-content">
                  <img
                    src="/smulogo.png"
                    alt="SMU Logo"
                    style={{
                      width: '100px',
                      marginBottom: '1rem',
                      borderRadius: '50%',
                    }}
                  />
                  <h2>Welcome to SMU Alumni Portal</h2>
                  <p>Stay connected. Stay informed.</p>
                </div>
              </div>
            )}

            <div className="login-carousel-indicators">
              {carouselSlides.map((_, index) => (
                <button
                  key={index}
                  className={`login-carousel-dot ${
                    index === currentSlide ? 'active' : ''
                  }`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Login Form */}
          <div className="login-form-container">
            <div className="logo-section">
              <img src="/smulogo.png" alt="SMU Logo" className="form-logo" />
              <h1 className="form-title">SMU Alumni Login</h1>
            </div>

            <form onSubmit={handleLogin} noValidate>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  required
                  aria-describedby="emailError"
                  autoComplete="email"
                />
                {emailTouched && !email && (
                  <div id="emailError" className="field-required">
                    Email is required.
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-field">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setPasswordTouched(true)}
                    required
                    aria-describedby="passwordError"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordTouched && !password && (
                  <div id="passwordError" className="field-required">
                    Password is required.
                  </div>
                )}
              </div>

              <button type="submit" className="login-button">
                LOGIN
              </button>
              {errorMessage && (
                <div className="login-error-message">{errorMessage}</div>
              )}

              <div className="login-footer">
                <p className="forgot-password">
                  <Link to="/forgotpassword">Forgot Password?</Link>
                </p>
                <p className="Register-text">
                  Don&apos;t have an account?{' '}
                  <Link to="/register">Register</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
